import { useCallback } from 'react'
import { useMutation } from '@apollo/client'
import {
  INQUIRE_LOYALTY,
  ADD_LOYALTY_REDEMPTION_TO_CART,
  REMOVE_LOYALTY_REDEMPTION_FROM_CART
} from '../../apollo/cart/cart.graphql'
import { useCart } from '@local/do-secundo-cart-provider'
import { dataByTypename } from '../../utils/apollo-helpers'
import { useGetOnCartOutOfStockCallback } from './useGetOnCartOutOfStockCallback'

export const loyaltyMutationHook =
  (graphqlMutation, dataKey) =>
  ({ onCompleted = () => {}, onError = () => {} } = {}) => {
    const { cartGuid } = useCart()
    const onCartOutOfStockCallback = useGetOnCartOutOfStockCallback()
    const [loyaltyMutateOriginal, apolloResponse] = useMutation(
      graphqlMutation,
      {
        variables: {
          input: {
            cartGuid
          }
        },
        onCompleted: (data) => {
          const { LoyaltyInquiryResponse, CartOutOfStockError } =
            dataByTypename(data[dataKey])
          if (LoyaltyInquiryResponse) {
            onCompleted(data)
          }

          if (CartOutOfStockError) {
            onCartOutOfStockCallback(CartOutOfStockError)
          }
        },
        onError
      }
    )

    const loyaltyMutate = useCallback(
      (mutationOptions) => {
        if (!cartGuid) return
        return loyaltyMutateOriginal(mutationOptions)
      },
      [cartGuid, loyaltyMutateOriginal]
    )

    return [loyaltyMutate, apolloResponse]
  }

export const useInquireLoyalty = loyaltyMutationHook(
  INQUIRE_LOYALTY,
  'loyaltyInquiry'
)

export const useAddRedemption = (options) => {
  const [addRedemptionOriginal, apolloResponse] = loyaltyMutationHook(
    ADD_LOYALTY_REDEMPTION_TO_CART,
    'addLoyaltyRedemption'
  )(options)
  const { cartGuid } = useCart()
  const addRedemption =
    /**
     * @param {string} redemptionGuid
     * @param {import('react-apollo').MutationFunctionOptions} [mutationOptions]
     */
    (redemptionGuid, mutationOptions) => {
      return addRedemptionOriginal({
        variables: { input: { cartGuid, redemptionGuid } },
        ...mutationOptions
      })
    }

  return [addRedemption, apolloResponse]
}

export const useRemoveRedemption = (options) => {
  const [removeRedemptionOriginal, apolloResponse] = loyaltyMutationHook(
    REMOVE_LOYALTY_REDEMPTION_FROM_CART,
    'removeLoyaltyRedemption'
  )(options)
  const { cartGuid } = useCart()
  const removeRedemption =
    /**
     * @param {string} redemptionGuid
     * @param {import('react-apollo').MutationFunctionOptions} [mutationOptions]
     */
    (redemptionGuid, mutationOptions) => {
      return removeRedemptionOriginal({
        variables: { input: { cartGuid, redemptionGuid } },
        ...mutationOptions
      })
    }
  return [removeRedemption, apolloResponse]
}
